import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { isDesignWorkflowNote } from "./liveElectricalDesign";

// An operating-current exceedance the designer has chosen: the simulation
// clips it on the I-V curve and saving is allowed. It is reported, quietly -
// a design with two strings on every MPPT of four inverters is forty-eight
// trackers, and the same orange sentence under each read as a failed design.
const isOperatingNote = (issue) => issue?.severity === "warning" && issue?.kind === "operating_limit";

function MetricsTable({ metrics = [], greek }) {
  // An accepted operating exceedance is a row like any other, in orange; a
  // metric whose limit is unknown stays out.
  const checked = metrics.filter((m) => m.status !== "warning" || m.exceeded);
  if (!checked.length) return null;
  const tr = (el, en) => greek ? el : en;
  return <Box component="table" sx={{ width: "100%", minWidth: 540, tableLayout: "fixed",
    borderCollapse: "collapse", fontSize: "0.875rem", lineHeight: 1.4, color: "text.primary",
    "& th, & td": { px: 1, py: 0.5, textAlign: "left", borderBottom: "1px solid", borderColor: "divider" },
    "& th": { fontWeight: 700, color: "text.primary", bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.2 : 0.1) },
    "& tbody tr:nth-of-type(odd)": { bgcolor: "background.paper" },
    "& tbody tr:nth-of-type(even)": { bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.1 : 0.045) },
    "& tbody td:nth-of-type(2), & tbody td:nth-of-type(3)": { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  }}>
    <colgroup><col style={{ width: "46%" }} /><col style={{ width: "18%" }} /><col style={{ width: "18%" }} /><col style={{ width: "18%" }} /></colgroup>
    <thead><tr><th scope="col">{tr("Κριτήριο", "Criterion")}</th><th scope="col">{tr("Τιμή", "Value")}</th><th scope="col">{tr("Όριο", "Limit")}</th><th scope="col">{tr("Έλεγχος", "Check")}</th></tr></thead>
    <tbody>{checked.map((m, i) => <tr key={i}>
      <td>{m.label}</td><td>{m.value} {m.unit}</td><td>{m.limit === "—" ? "—" : `${m.lower ? "≥" : "≤"} ${m.limit} ${m.unit}`}</td>
      <Box component="td" sx={{ color: m.status === "error" ? "error.main" : m.exceeded ? "warning.main" : "text.primary",
        fontWeight: m.status === "error" || m.exceeded ? 700 : 500 }}>
        {m.status === "error" || m.exceeded ? tr("Εκτός ορίου", "Outside limit") : m.status === "info" ? tr("Ενημερωτικό", "For reference") : tr("Εντός ορίου", "Within limit")}
      </Box>
    </tr>)}</tbody>
  </Box>;
}

// Only actionable issues appear beside the controls. Checked measurements are
// available on demand; catalogue housekeeping never becomes a design warning.
export function LiveMpptFeedback({ row, greek }) {
  if (!row?.used) return null;
  const visible = row.issues.filter((i) => i.field !== "length" && !isDesignWorkflowNote(i));
  if (!visible.length) return null;
  return <Box aria-live="polite">
    {visible.map((i, index) => isOperatingNote(i)
      ? <Typography key={index} variant="caption" component="p" color="text.secondary"
          sx={{ mt: 0.5, mb: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", flex: "none", bgcolor: "warning.light" }} />
          {i.text}
        </Typography>
      : <Typography key={index} variant="body2" component="p"
          color={`${i.severity}.main`} sx={{ mt: 0.75, mb: 0 }}>
          {i.severity === "error" ? (greek ? "Διόρθωση: " : "Correction: ") : (greek ? "Προσοχή: " : "Note: ")}{i.text}
        </Typography>)}
  </Box>;
}

export function LiveDesignStatus({ state, preview, greek, onRetry }) {
  const tr = (el, en) => greek ? el : en;
  if (state.status === "idle") return null;
  if (state.status === "loading") return <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CircularProgress size={14} /><Typography variant="caption">{tr("Φόρτωση ορίων ηλεκτρολογικού ελέγχου…", "Loading electrical limits…")}</Typography>
  </Box>;
  if (state.status === "failed") return <Alert severity="warning" sx={{ py: 0 }}
    action={<Button size="small" onClick={onRetry}>{tr("Επανάληψη", "Retry")}</Button>}>
    {tr("Οι άμεσες ενδείξεις δεν είναι διαθέσιμες. Ο τελικός έλεγχος θα γίνει κανονικά στην αποθήκευση.", "Live hints are unavailable. The final check will still run when saving.")}
  </Alert>;
  if (!state.data?.ready) return <Alert severity="error" sx={{ py: 0 }}>
    {(state.data?.errors || []).map((issue, i) => <Typography key={i} variant="body2">{issue.message?.[greek ? "el" : "en"]}</Typography>)}
  </Alert>;
  if (!preview) return null;
  const actionableWarnings = preview.warnings.filter((i) => !isDesignWorkflowNote(i) && !isOperatingNote(i));
  const operatingNotes = preview.warnings.filter(isOperatingNote);
  const clippedMppts = new Set(operatingNotes.filter((i) => i.mppt).map((i) => `${i.inverter}:${i.mppt}`)).size;
  // Operating exceedances are rows of the checks tables below, not a list.
  const visibleIssues = preview.issues.filter((i) => !isDesignWorkflowNote(i) && !isOperatingNote(i));
  const location = (issue) => [
    issue.inverter ? tr(`Αντιστροφέας ${issue.inverter}`, `Inverter ${issue.inverter}`) : null,
    issue.mppt ? `MPPT ${issue.mppt}` : null,
  ].filter(Boolean).join(" · ");
  return <Box>
    <Typography component="div" variant="body2" aria-live="polite"
      color={preview.errors.length ? "error.main" : actionableWarnings.length ? "warning.main" : "text.primary"}>
      {!preview.hasStrings ? tr("Συνδέστε μία είσοδο για να δείτε τις άμεσες ενδείξεις.", "Connect an input to see live hints.")
        : preview.errors.length ? tr(`Άμεσος έλεγχος: ${preview.errors.length} προβλήματα προς διόρθωση.`, `Live check: ${preview.errors.length} issues to correct.`)
        : actionableWarnings.length ? tr("Χωρίς υπερβάσεις στα διαθέσιμα όρια· υπάρχουν προειδοποιήσεις προς έλεγχο.", "No available limits are exceeded; some warnings need review.")
        : operatingNotes.length ? tr("Χωρίς υπερβάσεις ορίων ασφαλείας.", "No safety limits are exceeded.")
        : tr("Δεν εντοπίζονται υπερβάσεις στα διαθέσιμα όρια.", "No available limits are exceeded.")}
      {" · "}{tr("Τελικός έλεγχος στην αποθήκευση.", "Final check on Save.")}
    </Typography>
    {/* The explanation, once, instead of under every tracker. */}
    {!preview.errors.length && operatingNotes.length > 0 && <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.25, mb: 0 }}>
      {clippedMppts
        ? tr(`Σε ${clippedMppts} MPPT το ρεύμα λειτουργίας ξεπερνά το όριο του αντιστροφέα. Η αποθήκευση επιτρέπεται· η προσομοίωση υπολογίζει την περικοπή πάνω στην καμπύλη I–V.`,
          `On ${clippedMppts} MPPT(s) the operating current exceeds the inverter's limit. Saving is allowed; the simulation calculates the clipping on the I–V curve.`)
        : tr("Το συνολικό ρεύμα λειτουργίας ξεπερνά το όριο του αντιστροφέα. Η αποθήκευση επιτρέπεται· η προσομοίωση υπολογίζει την περικοπή πάνω στην καμπύλη I–V.",
          "The total operating current exceeds the inverter's limit. Saving is allowed; the simulation calculates the clipping on the I–V curve.")}
    </Typography>}
    {preview.errors.filter((i) => !i.inverter).map((i, index) => <Typography key={index} variant="caption"
      component="p" color="error.main" sx={{ my: 0.5 }}>{i.text}</Typography>)}
    <Box component="details" sx={{ mt: 0.5, "& > summary": { cursor: "pointer", color: "primary.main", fontSize: "0.875rem",
      fontWeight: 700, lineHeight: 1.5, py: 0.5, width: "fit-content", borderRadius: 0.5,
      "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 3 } } }}>
      <Box component="summary">{tr("Λεπτομέρειες ελέγχου", "Check details")}</Box>
      <Box sx={{ mt: 0.5, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1,
        bgcolor: "background.paper", maxHeight: 360, overflow: "auto" }}>
        {visibleIssues.length > 0 && <Box component="ul" sx={{ m: 0, mb: 0.75, pl: 2, fontSize: "0.875rem" }}>
          {visibleIssues.map((i, index) => <Box component="li" key={index} sx={{ mb: 0.5,
            color: i.severity === "error" ? "error.main" : "warning.main" }}>
            {location(i) ? `${location(i)}: ` : ""}{i.text}
          </Box>)}
        </Box>}
        {preview.inverters.map((inv, invIndex) => inv.strings > 0 && <Box key={invIndex} sx={{ "& + &": { mt: 1 } }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.25 }}>{tr(`Αντιστροφέας ${invIndex + 1} — σύνολα`, `Inverter ${invIndex + 1} — totals`)}</Typography>
          <Box sx={{ overflowX: "auto" }}><MetricsTable metrics={[
            ...inv.metrics,
            ...(inv.ratio == null ? [] : [{ label: tr("Λόγος DC/AC", "DC/AC ratio"),
              value: inv.ratio.toLocaleString(greek ? "el-GR" : "en-GB", { maximumFractionDigits: 2 }),
              limit: "—", unit: "", status: "info" }]),
          ]} greek={greek} /></Box>
          {inv.mppts.map((row, mpptIndex) => row.used > 0 && <Box key={mpptIndex} sx={{ mt: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>{`MPPT ${mpptIndex + 1}`}</Typography>
            <Typography variant="body2" component="div" color="text.primary" sx={{ mb: 0.25 }}>
              {state.data?.inverter?.parallel_strings
                ? tr(`Παράλληλα strings: ${row.used} · έως ${row.maxStrings ?? "—"} με βάση τα διαθέσιμα όρια και αυτό το πλαίσιο.`,
                  `Parallel strings: ${row.used} · up to ${row.maxStrings ?? "—"} based on available limits and this module.`)
                : tr(`Συνδεδεμένες είσοδοι: ${row.used} · έως ${row.maxStrings ?? "—"} με βάση τα διαθέσιμα όρια και αυτό το πλαίσιο.`,
                  `Connected inputs: ${row.used} · up to ${row.maxStrings ?? "—"} based on available limits and this module.`)}
            </Typography>
            <Box sx={{ overflowX: "auto" }}><MetricsTable metrics={row.metrics} greek={greek} /></Box>
          </Box>)}
        </Box>)}
      </Box>
    </Box>
  </Box>;
}
